'use client';

import React, { Fragment } from 'react';

import type { CopyActionKey, CopyNode, CopyPart } from '@/types/copy';

import { cn } from '@/lib/utils';

export const INLINE_ACTION_CLASS = 'cursor-pointer underline decoration-dashed underline-offset-[3px]';

export type CopyActions = Partial<Record<CopyActionKey, () => void>>;

type CopyProps = {
	value: CopyNode;
	actions?: CopyActions;
	className?: string;
};

function renderNode(node: CopyNode, actions: CopyActions | undefined): React.ReactNode {
	if (Array.isArray(node)) {
		return node.map((part, index) => (
			<Fragment key={index}>{renderPart(part, actions)}</Fragment>
		));
	}

	return renderPart(node, actions);
}

function renderPart(part: CopyPart, actions: CopyActions | undefined): React.ReactNode {
	if (typeof part === 'string') {
		return part;
	}

	if ('em' in part) {
		return <strong className="font-medium">{renderNode(part.em, actions)}</strong>;
	}

	const onClick = actions?.[part.action];

	// An action with no handler would render as a dead button, so surface it in dev
	// and fall back to plain text.
	if (!onClick) {
		if (process.env.NODE_ENV !== 'production') {
			console.error(`<CopyText>: no handler supplied for action "${part.action}"`);
		}

		return part.label;
	}

	return (
		<button
			type="button"
			onClick={onClick}
			className={INLINE_ACTION_CLASS}
		>
			{part.label}
		</button>
	);
}

/**
 * Renders a registry copy value.
 *
 * Deliberately dumb: shape to JSX, nothing else. Copy that varies by plan or role
 * is a different registry key, never a branch in here.
 */
export const CopyText = React.memo(function CopyText({ value, actions, className }: CopyProps) {
	// `\n` in a registry string is a real line break, hence whitespace-pre-line.
	return <span className={cn('whitespace-pre-line', className)}>{renderNode(value, actions)}</span>;
});
